import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { ColumnDataType } from 'app/components/favourite-layout/favourite/common/favourite-interfaces';
import { CaseTableSummarySection, DisplayDateValue, 
  DisplayResultValue } from 'app/area/case-report/case-table-view/components/case-table-view-cells';
import { CASE_TABLE_VIEW_DATE_FORMAT, getColumnData } 
from 'app/area/case-report/case-table-view/components/case-table-view-util';
import { ComplexValue, Entity, FailedDomainConstraintMetaData, UserMessage } from 'app/types-shared';
import { getCaseIdField, getCaseType } from 'app/area/case-report/case-table-view/components/case-table-view-rows';
import { CaseTableViewMeatballMenu } from 'app/area/case-report/case-table-view/components/case-table-view-meatball-menu';
import { withCallsToActions } from 'app/area/case/features';
import { getDomainAttributesForRow } from 'app/area/case-report/case-table-view/map-summary-section-column';
import { CaseTableViewContext } from 'app/area/case-report/case-table-view/case-table-view-context';
import { caseTableViewExternalUrlHook } from 'app/area/case-report/case-table-view/hooks/case-table-view-external-url-hook';
import { AddNewConnectedCasesWireData, ChangeCaseStatusWireData } from 'app/area/case/features/calls-to-actions/types';
import { getUrlOpenCase } from 'app/components/helpers';


interface CardProps {
  index: number;
  row: Entity[];
  singleSupplierUrl?: string;
  callsToActions?: (caseId: number) => { changeStatus: ChangeCaseStatusWireData; addNewConnectedCases: AddNewConnectedCasesWireData[] };
  onChangeCaseStatus?: (caseId: number, status: ComplexValue<number>) => 
      {userMessages: string | UserMessage[]; warnings: FailedDomainConstraintMetaData[]; validationErrors: string[]};
  changingCaseStatus?: boolean;
}

const Card = React.memo(({ index, row, singleSupplierUrl }: CardProps) => {
    const { columns, locale,  domainAttributes, location, searchParams } = useContext(CaseTableViewContext);
    const caseId = getCaseIdField(row);
    const caseType = getCaseType(row?.[0]);
    const domainAttributesForRow = useMemo(() => getDomainAttributesForRow({ domainAttributes, caseType }), [caseType]);
    const renderRow = useCallback(() => {
      return columns.map((column, columnIndex) => {
        if ((column.dataType === ColumnDataType.SummarySection) && column.summarySectionTag) {
          return (
            <tr key={`tr-${index}-${columnIndex}`}>
            <th>{column.shortText}</th>
              <td key={`${index}-${columnIndex}`}>
                <CaseTableSummarySection
                  componentId={`data-column-${index}-${columnIndex}`}
                  column={column}
                  locale={locale}
                  row={row}
                  caseId={caseId}
                  domainAttributesForRow={domainAttributesForRow}
                  location={location}
                  searchParams={searchParams}
                />
            </td>
            </tr>
          );
        } else {
          const itemTd = getColumnData(row, column);
        return (
          <tr key={`tr-${index}-${columnIndex}`}>
            <th>{column.shortText}</th>
            <td>
              {columnIndex === 0 && <div data-ref={columnIndex}>
                <CaseTableViewMeatballMenu 
                  caseId={caseId} 
                  singleSupplierUrl={singleSupplierUrl}
                  />
              </div>}
              {column.dataType === ColumnDataType.Date ?
                <DisplayDateValue
                  cells={itemTd}
                  componentId={`data-column-${index}-${columnIndex}`}
                  dateTimeFormatter={CASE_TABLE_VIEW_DATE_FORMAT}
                  locale={locale}
                  column={column}
                  key={`${index}-${columnIndex}`}
                  isMobileView={true}
                />
                : <DisplayResultValue
                  cells={itemTd}
                  column={column}
                  componentId={`data-column-${index}-${columnIndex}`}
                  key={`${index}-${columnIndex}`}
                  isMobileView={true}
                  toCaseView={() => getUrlOpenCase(caseId, location.pathname, searchParams)}
                />
              }
            </td>
          </tr>
        );
      }
    }
    );
    }, [row, columns, locale]);

    return (
      <tbody>
        {renderRow()}
      </tbody>
    );
});


export function CaseTableMobileView() {
  const { rows } = useContext(CaseTableViewContext);
  const { singleSupplierUrl, loadSingleSupplierUrl } = caseTableViewExternalUrlHook();

  useEffect(() => {
    loadSingleSupplierUrl();
  }, [loadSingleSupplierUrl]); 
    
  if (!rows?.length) {
    return null;
  }
  const UpdatedCard = withCallsToActions(Card);
  return (
    <table className="case-table-mobile-view">
      {rows.map((row, rowIndex) => (
        <UpdatedCard key={rowIndex} row={row} index={rowIndex} singleSupplierUrl={singleSupplierUrl} />
      ))}
    </table>
  );
}
